import React, { useEffect, useMemo, useRef } from "react";
import {
  Alert,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material";
import {
  MAX_ACTIVE_PROFILES,
  useScoringStore,
  V1_PREFIX,
  v1SourceId,
  v2SourceId,
} from "@/store/scoringStore";
import { getTenantId } from "@/utils/auth";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import { FieldsAutocomplete } from "./FieldsAutocomplete";
import { EXPORT_INCLUSION_TYPES } from "./types";

export const MAX_AI_SCORE_MODELS = 20;

const AI_SCORE_TIERS = ["A", "B", "C", "D", "F", "DQ"];

const OPTIONS = [
  {
    label: "Include only selected scoring profile(s)",
    value: EXPORT_INCLUSION_TYPES.SELECTED,
  },
  { label: "Do not include any scores", value: EXPORT_INCLUSION_TYPES.NONE },
];

interface Props {
  inclusion: EXPORT_INCLUSION_TYPES;
  setInclusion: (value: EXPORT_INCLUSION_TYPES) => void;
  selectedSourceIds: string[];
  setSelectedSourceIds: (sourceIds: string[]) => void;
  selectedTiers: string[];
  setSelectedTiers: (tiers: string[]) => void;
}

export const AIScoresExportOptions: React.FC<Props> = ({
  inclusion,
  setInclusion,
  selectedSourceIds,
  setSelectedSourceIds,
  selectedTiers,
  setSelectedTiers,
}) => {
  const tenantId = getTenantId();
  const profiles = useScoringStore((s) => s.profiles);
  const models = useScoringStore((s) => s.models);
  const isLoading = useScoringStore((s) => s.isModelsLoading);
  const error = useScoringStore((s) => s.modelsError);
  const fetchModels = useScoringStore((s) => s.fetchModels);

  const requestedTenant = useRef<string | null>(null);
  useEffect(() => {
    if (
      tenantId &&
      models.length === 0 &&
      requestedTenant.current !== tenantId
    ) {
      requestedTenant.current = tenantId;
      fetchModels(tenantId);
    }
  }, [tenantId, models.length, fetchModels]);

  const options = useMemo(
    () => [
      ...profiles.map((p) => ({ value: v1SourceId(p.id), label: p.name })),
      ...models.map((m) => ({ value: v2SourceId(m.family), label: m.name })),
    ],
    [profiles, models]
  );

  const basicCount = selectedSourceIds.filter((sid) =>
    sid.startsWith(V1_PREFIX)
  ).length;
  const aiCount = selectedSourceIds.length - basicCount;
  const atLimit =
    basicCount >= MAX_ACTIVE_PROFILES || aiCount >= MAX_AI_SCORE_MODELS;
  const noneSelected =
    inclusion === EXPORT_INCLUSION_TYPES.SELECTED &&
    selectedSourceIds.length === 0;

  return (
    <Stack
      spacing={2}
      sx={{ width: "70%", paddingTop: "40px" }}
      data-testid="export-ai-scores-section"
    >
      <Typography variant="h5" fontWeight={500} fontSize={16}>
        AI Scores
      </Typography>
      {!tenantId && (
        <Alert severity="info" data-testid="export-ai-scores-no-tenant">
          AI models need a tenant; only basic scoring profiles are listed.
        </Alert>
      )}
      <ExportDataTypeInclusionSelector
        dataType="scoring profiles"
        options={OPTIONS}
        inclusion={inclusion}
        setInclusion={setInclusion}
      />
      {inclusion !== EXPORT_INCLUSION_TYPES.NONE && isLoading && (
        <CircularProgress
          size={20}
          aria-label="Loading scoring profiles"
          data-testid="export-ai-scores-loading"
        />
      )}
      {inclusion !== EXPORT_INCLUSION_TYPES.NONE && error && (
        <Alert severity="error" data-testid="export-ai-scores-error">
          {error}
        </Alert>
      )}
      {inclusion !== EXPORT_INCLUSION_TYPES.NONE &&
        !isLoading &&
        !error &&
        options.length === 0 && (
          <Alert severity="info" data-testid="export-ai-scores-empty">
            No scoring profiles available.
          </Alert>
        )}
      {inclusion === EXPORT_INCLUSION_TYPES.SELECTED && (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Selected profiles
          </Typography>
          <FieldsAutocomplete
            id="export-ai-scores-models"
            options={options}
            selectedValues={selectedSourceIds}
            onChange={(sourceIds) => {
              const basic = sourceIds.filter((sid) =>
                sid.startsWith(V1_PREFIX)
              ).length;
              if (
                basic <= MAX_ACTIVE_PROFILES &&
                sourceIds.length - basic <= MAX_AI_SCORE_MODELS
              ) {
                setSelectedSourceIds(sourceIds);
              }
            }}
            groupBy={(o) =>
              o.value.startsWith(V1_PREFIX) ? "Basic" : "Advanced ✦ AI"
            }
            placeholder="Search profiles..."
            disabled={options.length === 0}
          />
          {noneSelected && (
            <Typography
              variant="caption"
              color="error"
              role="alert"
              data-testid="export-ai-scores-selection-error"
            >
              Select at least one scoring profile.
            </Typography>
          )}
          {atLimit && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="export-ai-scores-limit"
            >
              {`Up to ${MAX_ACTIVE_PROFILES} basic profiles and ${MAX_AI_SCORE_MODELS} AI models per data feed.`}
            </Typography>
          )}
        </Stack>
      )}
      {inclusion === EXPORT_INCLUSION_TYPES.SELECTED && aiCount > 0 && (
        <Stack spacing={0.5} data-testid="export-ai-scores-tiers">
          <Typography variant="body2" color="text.secondary">
            AI model tiers (none selected includes all tiers)
          </Typography>
          <FormGroup row>
            {AI_SCORE_TIERS.map((tier) => (
              <FormControlLabel
                key={tier}
                label={tier}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedTiers.includes(tier)}
                    onChange={(_, checked) =>
                      setSelectedTiers(
                        checked
                          ? AI_SCORE_TIERS.filter(
                              (t) => t === tier || selectedTiers.includes(t)
                            )
                          : selectedTiers.filter((t) => t !== tier)
                      )
                    }
                    data-testid={`export-ai-scores-tier-${tier}-checkbox`}
                  />
                }
              />
            ))}
          </FormGroup>
        </Stack>
      )}
    </Stack>
  );
};
